import * as React from 'react';
// mui
import {
  CardHeader,
  Collapse,
  ListItemButton,
  TextField,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
  Typography,
} from '@mui/material';
// icons
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FlagIcon from '@mui/icons-material/Flag';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// types
import Task from '../../../interfaces/Task.interface';
// axios
import api from '../../../axios';
// components
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';
import { convertEnergyToString, convertPriorityToString, convertTimeToMinutesString } from '../../common/propertyValueToString';
import ItemContentDate from '../../common/ItemContentDate';
import MiniTaskList from '../../TasksPage/TaskItem/MiniTaskList';
import { useSelector } from 'react-redux';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import ExcuseDurationSelect from './ExcuseDurationSelect';

interface TaskCardProps {
  taskData: Task;
  onMoveToNextTask: () => void;
}

/*
 * taskdata is passed in as an object
 * task mini tasks are pulled from redux
 * beware of this difference
 */
export default function TaskCard(props: TaskCardProps) {
  // props
  const taskData = props.taskData;
  const moveToNextTaskCallback = props.onMoveToNextTask;
  const taskFromRedux: Task | undefined = useSelector(selectTaskById(taskData.id));
  const uncompleteMiniTasks: number = taskFromRedux
    ? taskFromRedux.miniTasks.filter((miniTask) => !miniTask.completed).length
    : 0;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // state
  const [ showExcuseForm, setShowExcuseForm ] = React.useState(false);
  const [ excuseValue, setExcuseValue ] = React.useState<String>('');
  const [ excuseLoading, setExcuseLoading ] = React.useState<boolean>(false);
  const [ completeLoading, setCompleteLoading ] = React.useState<boolean>(false);
  const [ excuseDuration, setExcuseDuration ] = React.useState<string>('');

  const handleExcuseSubmission = () => {
    setExcuseLoading(true);
    api.post('/tasks/excuses/create', {
      content: excuseValue,
      taskId: taskData.id,
      duration: excuseDuration,
      date: dayjs.utc().toISOString()
    })
      .then((res) => {
        console.log(res);
        // reset excuse area in case component is being reloading with new task data
        setShowExcuseForm(false);
        setExcuseValue('');
        
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setExcuseLoading(false);
        setExcuseDuration('');
      });
  }

  const handleComplete = () => {
    setCompleteLoading(true);
    api.put(`/tasks/normalized/update/${taskData.id}`, {
      completed: true
    })
      .then((res) => {
        console.log(res);
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setCompleteLoading(false);
        setExcuseDuration('');
      });
  }

  const actions = (
    <React.Fragment>
      <Button
        variant='contained'
        loading={completeLoading}
        onClick={handleComplete}
        disabled={uncompleteMiniTasks !== 0}
        sx={{ width: isMobile ? '100%' : 'auto', mb: isMobile ? 1 : 0}}
      >
        <DoneIcon sx={{ pr: 1 }} /> Complete
      </Button>
    </React.Fragment>
  )

  return (
    <Card sx={{ width: '100%', m: 0, p: isMobile ? 0 : 2, pt: isMobile ? 0 : 2, minHeight: 'auto', border: isMobile ? 'none' : '' }} variant='outlined'>
      <CardHeader 
        title={taskData.title}
        subheader={
          <React.Fragment>
            {taskData.dueDate &&
              <ItemContentDate itemData={taskData} itemType="task" />
            }
            <FlagIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              ml: taskData.dueDate ? '4px' : ''
            }} />{convertPriorityToString(taskData.priority)} 
            <ElectricBoltIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              marginLeft: '8px'
            }} />{convertEnergyToString(taskData.energy)} 
            <AccessAlarmIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              marginLeft: '8px'
            }} />{convertTimeToMinutesString(taskData.time)}
          </React.Fragment>
        }
        action={ isMobile ? null : actions }
      />
      <CardContent sx={{ pt: 0 }}>
        <ItemBreadcrumbs
          currentItem={taskData}
          itemType="task"
          includeCurrentItem={true}
        />

        { taskData.description && 
          <Typography>{taskData.description.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}<br /></Typography>
        }

        { taskData.miniTasks.length > 0 && (
          <>
            <MiniTaskList
              parentTaskId={taskData.id}
              padding={0}
              isCompletedList={false}
            />
            <MiniTaskList
              parentTaskId={taskData.id}
              padding={0}
              isCompletedList={true}
            />
          </>
        )}

        {/* show action buttons here on mobile */}
        { isMobile && actions }

        <ListItemButton onClick={() => setShowExcuseForm(!showExcuseForm)} sx={{ color: 'grey', width: '100%', mt: 2 }}>
          {showExcuseForm ? <KeyboardArrowDownIcon sx={{ pr: 1 }} /> : <KeyboardArrowUpIcon sx={{ pr: 1 }} />} Cant Complete / Don't Want To
        </ListItemButton>
        <Collapse in={showExcuseForm}>
          <TextField
            label="Excuse"
            value={excuseValue}
            onChange={(event) => setExcuseValue(event.target.value)}
            sx={{ paddingBottom: 2, mt: 2 }}
            multiline
            rows={4}
            fullWidth
          />

          <ExcuseDurationSelect
            onChange={(intervalString: string) => { setExcuseDuration(intervalString); }}
          />

          <Button
            loading={excuseLoading}
            onClick={handleExcuseSubmission}
            disabled={!excuseDuration || !excuseValue}
            variant='outlined'
            fullWidth
          >
            <ClearIcon sx={{ pr: 1}} /> Submit Excuse 
          </Button>
        </Collapse>
      </CardContent>
    </Card>
  );
}